import React from 'react';

/**
 * Page de politique de confidentialité RGPD
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Politique de Confidentialité
        </h1>

        <p className="text-sm text-gray-600 mb-8">
          Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
        </p>

        <div className="prose prose-blue max-w-none">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Match Compétences s'engage à protéger la vie privée de ses utilisateurs.
              Cette politique de confidentialité explique comment nous collectons,
              utilisons, partageons et protégeons vos données personnelles conformément
              au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          {/* Responsable du traitement */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              2. Responsable du traitement
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Match Compétences</strong></p>
              <p className="text-gray-700">Email : contact@match-competences.com</p>
              <p className="text-gray-700">DPO : dpo@match-competences.com</p>
            </div>
          </section>

          {/* Données collectées */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              3. Données collectées
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Données d'identification</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Rôle (responsable pédagogique, collaborateur, invité)</li>
              <li>Date de création du compte</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-4">3.2 Données d'utilisation</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Logs de connexion (date, heure, IP)</li>
              <li>Historique des actions (projets créés, imports/exports)</li>
              <li>Préférences utilisateur (thème, langue, notifications)</li>
              <li>Statistiques d'utilisation anonymisées</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-4">3.3 Données techniques</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Adresse IP</li>
              <li>Type de navigateur et version</li>
              <li>Système d'exploitation</li>
              <li>Cookies (avec votre consentement)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-4">3.4 Données de paiement</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Informations de facturation (nom, adresse)</li>
              <li>Historique des paiements</li>
              <li><strong>Note importante :</strong> Les données de carte bancaire ne sont JAMAIS
                stockées sur nos serveurs. Elles sont traitées directement et de manière sécurisée
                par notre prestataire Stripe (conforme PCI DSS niveau 1).
              </li>
            </ul>
          </section>

          {/* Finalités du traitement */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              4. Finalités du traitement
            </h2>
            <p className="text-gray-700 mb-4">Vos données sont collectées pour :</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Gestion du compte :</strong> Création, authentification, gestion du profil</li>
              <li><strong>Fourniture du service :</strong> Accès aux fonctionnalités de la plateforme</li>
              <li><strong>Communication :</strong> Emails transactionnels, notifications importantes</li>
              <li><strong>Facturation :</strong> Traitement des paiements, émission de factures</li>
              <li><strong>Amélioration du service :</strong> Analytics, détection de bugs, optimisations</li>
              <li><strong>Sécurité :</strong> Prévention de la fraude, audit des actions</li>
              <li><strong>Obligations légales :</strong> Respect des lois fiscales et comptables</li>
            </ul>
          </section>

          {/* Base légale */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              5. Base légale du traitement
            </h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Exécution du contrat :</strong> Pour fournir le service souscrit</li>
              <li><strong>Consentement :</strong> Pour les cookies analytics et marketing</li>
              <li><strong>Intérêt légitime :</strong> Pour la sécurité et l'amélioration du service</li>
              <li><strong>Obligation légale :</strong> Pour la conservation des données de facturation</li>
            </ul>
          </section>

          {/* Partage des données */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              6. Partage des données
            </h2>
            <p className="text-gray-700 mb-4">
              Vos données peuvent être partagées avec des tiers de confiance uniquement dans les cas suivants :
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">6.1 Sous-traitants techniques</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Stripe :</strong> Traitement des paiements (conforme PCI DSS)</li>
              <li><strong>Brevo :</strong> Envoi d'emails transactionnels</li>
              <li><strong>Sentry :</strong> Monitoring des erreurs</li>
              <li><strong>Matomo :</strong> Analytics respectueux de la vie privée (auto-hébergé)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-4">6.2 Autorités légales</h3>
            <p className="text-gray-700">
              En cas de réquisition judiciaire ou d'obligation légale.
            </p>

            <p className="text-gray-700 mt-4 font-medium">
              ⚠️ Nous ne vendons JAMAIS vos données à des tiers.
            </p>
          </section>

          {/* Durée de conservation */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              7. Durée de conservation
            </h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Données de compte actif :</strong> Pendant toute la durée du contrat + 3 ans</li>
              <li><strong>Données de facturation :</strong> 10 ans (obligation légale)</li>
              <li><strong>Logs d'audit :</strong> 1 an maximum</li>
              <li><strong>Cookies analytics :</strong> 13 mois maximum</li>
              <li><strong>Compte inactif :</strong> Suppression après 3 ans sans connexion (avec alerte préalable)</li>
            </ul>
          </section>

          {/* Droits RGPD */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              8. Vos droits RGPD
            </h2>
            <p className="text-gray-700 mb-4">Vous disposez des droits suivants :</p>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Droit d'accès</h4>
                <p className="text-gray-700">Obtenir une copie de vos données personnelles</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Droit de rectification</h4>
                <p className="text-gray-700">Corriger des données inexactes ou incomplètes</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Droit à l'effacement</h4>
                <p className="text-gray-700">Demander la suppression de vos données (droit à l'oubli)</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Droit à la limitation</h4>
                <p className="text-gray-700">Limiter le traitement de vos données dans certains cas</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Droit à la portabilité</h4>
                <p className="text-gray-700">Récupérer vos données dans un format structuré (JSON, CSV)</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Droit d'opposition</h4>
                <p className="text-gray-700">S'opposer au traitement de vos données pour motif légitime</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">✓ Retrait du consentement</h4>
                <p className="text-gray-700">Retirer votre consentement aux cookies et analytics à tout moment</p>
              </div>
            </div>

            <p className="text-gray-700 mt-6">
              <strong>Comment exercer vos droits :</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Directement depuis votre espace paramètres (Compte → Confidentialité)</li>
              <li>Par email : <a href="mailto:dpo@match-competences.com" className="text-blue-600 hover:underline">dpo@match-competences.com</a></li>
              <li>Délai de réponse : 1 mois maximum</li>
            </ul>
          </section>

          {/* Sécurité */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              9. Sécurité des données
            </h2>
            <p className="text-gray-700 mb-4">
              Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Chiffrement HTTPS (TLS 1.3) pour toutes les communications</li>
              <li>Chiffrement des données sensibles au repos (AES-256)</li>
              <li>Authentification forte avec double facteur (2FA)</li>
              <li>Contrôle d'accès basé sur les rôles (RBAC)</li>
              <li>Surveillance continue et détection d'intrusions (WAF Cloudflare)</li>
              <li>Sauvegardes quotidiennes chiffrées</li>
              <li>Audits de sécurité réguliers</li>
              <li>Formation du personnel à la sécurité des données</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              10. Cookies
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mb-3">10.1 Cookies strictement nécessaires</h3>
            <p className="text-gray-700 mb-2">
              Ces cookies sont indispensables au fonctionnement du site (pas de consentement requis) :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Session utilisateur (JWT token)</li>
              <li>Préférences de langue</li>
              <li>Panier / formulaires en cours</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-4">10.2 Cookies analytics (optionnels)</h3>
            <p className="text-gray-700 mb-2">
              Avec votre consentement, nous utilisons Matomo (auto-hébergé, conforme RGPD) :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Mesure d'audience anonymisée</li>
              <li>Durée de vie : 13 mois</li>
              <li>Respect du Do Not Track</li>
              <li>Pas de partage avec des tiers</li>
            </ul>

            <p className="text-gray-700 mt-4">
              <strong>Gestion des cookies :</strong> Vous pouvez accepter/refuser les cookies via le bandeau
              de consentement ou modifier vos préférences dans Paramètres → Confidentialité.
            </p>
          </section>

          {/* Transferts hors UE */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              11. Transferts hors Union Européenne
            </h2>
            <p className="text-gray-700 mb-4">
              Nos serveurs sont hébergés en France (OVH). Certains sous-traitants peuvent être situés hors UE :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Stripe (USA) :</strong> Clauses contractuelles types + certification PCI DSS</li>
              <li><strong>Sentry (USA) :</strong> Clauses contractuelles types + accord de traitement des données</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Ces transferts sont encadrés par des garanties appropriées conformément au RGPD.
            </p>
          </section>

          {/* Modifications */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              12. Modifications de la politique
            </h2>
            <p className="text-gray-700">
              Nous pouvons modifier cette politique de confidentialité. En cas de changement substantiel,
              vous serez informé par email et/ou notification sur la plateforme 30 jours avant l'entrée en vigueur.
            </p>
          </section>

          {/* Réclamation */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              13. Réclamation
            </h2>
            <p className="text-gray-700 mb-4">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez déposer une réclamation auprès de :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 font-semibold">CNIL (Commission Nationale de l'Informatique et des Libertés)</p>
              <p className="text-gray-700">3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07</p>
              <p className="text-gray-700">Tél : 01 53 73 22 22</p>
              <p className="text-gray-700">Web : <a href="https://www.cnil.fr" className="text-blue-600 hover:underline">www.cnil.fr</a></p>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-8 bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              14. Contact
            </h2>
            <p className="text-gray-700 mb-4">
              Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits :
            </p>
            <div className="space-y-2">
              <p className="text-gray-700">
                <strong>Email DPO :</strong>{' '}
                <a href="mailto:dpo@match-competences.com" className="text-blue-600 hover:underline">
                  dpo@match-competences.com
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Support :</strong>{' '}
                <a href="mailto:contact@match-competences.com" className="text-blue-600 hover:underline">
                  contact@match-competences.com
                </a>
              </p>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Match Compétences. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
